// src/components/villa/VillaFilter.jsx
import React, { useState } from 'react';

const VillaFilter = ({ 
  filters, 
  onFilterChange, 
  categories = [], 
  locations = [], 
  facilities = [],
  onClearFilters 
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleInputChange = (field, value) => {
    onFilterChange({ [field]: value });
  };

  const handlePriceChange = (field, value) => {
    // Remove non-numeric characters and convert to number
    const numericValue = value.replace(/[^\d]/g, '');
    onFilterChange({ [field]: numericValue });
  };

  const formatPrice = (value) => {
    if (!value) return '';
    return new Intl.NumberFormat('id-ID').format(value);
  };

  const priceRanges = [
    { label: 'Semua Harga', min: '', max: '' },
    { label: 'Di bawah 1 Juta', min: '', max: '1000000' },
    { label: '1 - 5 Juta', min: '1000000', max: '5000000' },
    { label: '5 - 10 Juta', min: '5000000', max: '10000000' },
    { label: '10 - 20 Juta', min: '10000000', max: '20000000' },
    { label: 'Di atas 20 Juta', min: '20000000', max: '' },
  ];

  const handlePriceRangeSelect = (min, max) => {
    // FIXED: Use underscore naming to match backend
    onFilterChange({ 
      min_price: min,
      max_price: max 
    });
  };

  // Handle facilities checkbox change
  const handleFacilityChange = (facilityId, isChecked) => {
    const currentFacilities = filters.facilities ? 
      filters.facilities.split(',').filter(f => f.trim() !== '') : [];
    
    let newFacilities;
    const facilityIdStr = facilityId.toString();
    
    if (isChecked) {
      // Add facility if not already present
      if (!currentFacilities.includes(facilityIdStr)) {
        newFacilities = [...currentFacilities, facilityIdStr];
      } else {
        newFacilities = currentFacilities;
      }
    } else {
      // Remove facility
      newFacilities = currentFacilities.filter(f => f !== facilityIdStr);
    }
    
    // Update filter with comma-separated string or empty string
    handleInputChange('facilities', newFacilities.length > 0 ? newFacilities.join(',') : '');
  };

  // Check if facility is selected
  const isFacilitySelected = (facilityId) => {
    if (!filters.facilities) return false;
    const currentFacilities = filters.facilities.split(',').filter(f => f.trim() !== '');
    return currentFacilities.includes(facilityId.toString());
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Filter</h3>
        <button
          onClick={onClearFilters}
          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          Reset
        </button>
      </div>

      {/* Category Filter */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Kategori Villa
        </label>
        <select
          value={filters.category}
          onChange={(e) => handleInputChange('category', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Semua Kategori</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.id.toString()}>
              {cat.name.charAt(0).toUpperCase() + cat.name.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {/* Location Filter */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Lokasi
        </label>
        <select
          value={filters.location}
          onChange={(e) => handleInputChange('location', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Semua Lokasi</option>
          {locations.map(loc => (
            <option key={loc.id} value={loc.id.toString()}>
              {loc.name}
            </option>
          ))}
        </select>
      </div>

      {/* Quick Price Ranges */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Rentang Harga
        </label>
        <div className="space-y-2">
          {priceRanges.map((range, index) => {
            // FIXED: Use underscore naming to match backend
            const isActive = filters.min_price === range.min && filters.max_price === range.max;
            return (
              <button
                key={index}
                onClick={() => handlePriceRangeSelect(range.min, range.max)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive
                    ? 'bg-blue-100 text-blue-800 border border-blue-300'
                    : 'hover:bg-gray-50 border border-gray-200'
                }`}
              >
                {range.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Custom Price Range */}
      <div>
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center justify-between w-full text-sm font-medium text-gray-700 hover:text-gray-900"
        >
          Harga Kustom
          <svg 
            className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {showAdvanced && (
          <div className="mt-3 space-y-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Harga Minimum (IDR)</label>
              <input
                type="text"
                value={formatPrice(filters.min_price)}
                onChange={(e) => handlePriceChange('min_price', e.target.value)}
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Harga Maksimum (IDR)</label>
              <input
                type="text"
                value={formatPrice(filters.max_price)}
                onChange={(e) => handlePriceChange('max_price', e.target.value)}
                placeholder="Tidak terbatas"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}
      </div>

      {/* Bedrooms */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Kamar Tidur
        </label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { value: '', label: 'Semua' },
            { value: '1', label: '1' },
            { value: '2', label: '2' },
            { value: '3', label: '3' },
            { value: '4', label: '4+' }
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => handleInputChange('bedrooms', option.value)}
              className={`py-2 px-3 text-sm rounded-lg border transition-colors ${
                filters.bedrooms === option.value
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'border-gray-300 hover:border-blue-300'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Bathrooms */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Kamar Mandi
        </label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { value: '', label: 'Semua' },
            { value: '1', label: '1' },
            { value: '2', label: '2' },
            { value: '3', label: '3' },
            { value: '4', label: '4+' }
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => handleInputChange('bathrooms', option.value)}
              className={`py-2 px-3 text-sm rounded-lg border transition-colors ${
                filters.bathrooms === option.value
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'border-gray-300 hover:border-blue-300'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Facilities Filter */}
      {facilities.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Fasilitas Populer
          </label>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {facilities.slice(0, 10).map((facility) => (
              <label key={facility.id} className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                <input
                  type="checkbox"
                  checked={isFacilitySelected(facility.id)}
                  onChange={(e) => handleFacilityChange(facility.id, e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                />
                <span className="ml-2 text-sm text-gray-700">{facility.name}</span>
              </label>
            ))}
          </div>
          
          {/* Show selected facilities count */}
          {filters.facilities && filters.facilities.length > 0 && (
            <div className="mt-2 text-xs text-blue-600">
              {filters.facilities.split(',').filter(f => f.trim() !== '').length} fasilitas dipilih
            </div>
          )}
        </div>
      )}

      {/* Featured Only */}
      <div>
        <label className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
          <input
            type="checkbox"
            checked={filters.featured === 'true'}
            onChange={(e) => handleInputChange('featured', e.target.checked ? 'true' : '')}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
          />
          <span className="ml-2 text-sm text-gray-700">Hanya Villa Unggulan</span>
        </label>
      </div>

      {/* Apply Filters Button (Mobile) */}
      <div className="block lg:hidden pt-4 border-t">
        <button
          onClick={() => {
            // Trigger refetch or close mobile filter
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Terapkan Filter
        </button>
      </div>
    </div>
  );
};

export default VillaFilter;